import type { ReactNode } from 'react'
import { FlatList, View, type FlatListProps, type ListRenderItem } from 'react-native'

import { Icon } from '@/src/elements/media/Icon'
import type { TPrayersApi, TUplift } from '@/src/features/intention/api/types/getIntentionPrayersApi.types'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useTranslate } from '@/src/lang/useTranslate'

import { CollapsableNote } from './CollapsableNote'
import { PrayerRow } from './PrayerRow'

export type TPrayersListProps = Pick<
  FlatList<TPrayersApi>['props'],
  | 'ListEmptyComponent'
  | 'ListHeaderComponent'
  | 'ListFooterComponent'
  | 'ItemSeparatorComponent'
  | 'data'
  | 'onEndReached'
  | 'testID'
> & {
  onAnswer: (params: TUplift & { prayerId: number }) => void
  onThank: (params: { prayerId: number; userId: number; index: number }) => void
  onSeeConversation: (userId: number) => void
  onPressReportAbuseUplift: (uplift: TUplift) => void
}

const keyExtractor: FlatListProps<TPrayersApi>['keyExtractor'] = (item) => item.id.toString()

export const PrayersList = ({
  onThank,
  onAnswer,
  onSeeConversation,
  onEndReached,
  testID,
  data,
  ListEmptyComponent,
  ListHeaderComponent,
  ListFooterComponent,
  ItemSeparatorComponent,
  onPressReportAbuseUplift,
}: TPrayersListProps): ReactNode => {
  const { t } = useTranslate()

  const renderItem: ListRenderItem<TPrayersApi> = ({ item: { id: prayerId, fromUser, uplift, messages }, index }) => {
    const userId = fromUser?.id ?? null
    const conversationIdFromMessage = messages[0]?.conversation?.id ?? undefined
    const conversationIdFromUplift = uplift?.replyToUplift?.conversation.id ?? undefined
    const conversationId = conversationIdFromMessage ?? conversationIdFromUplift
    const hasThanked = messages.length > 0
    const hasRepliedToUplift = conversationIdFromUplift !== undefined

    return (
      <>
        <PrayerRow
          onThank={
            userId
              ? () => {
                  onThank({ prayerId, userId, index })
                }
              : undefined
          }
          conversationId={conversationId}
          onSeeConversation={onSeeConversation}
          onAnswerPress={
            uplift
              ? () => {
                  onAnswer({ ...uplift, prayerId })
                }
              : undefined
          }
          labels={{
            thank: t('words.thank'),
            thankYouSent: t('words.thanked'),
            answer: t('words.answer'),
            see: t('words.see'),
          }}
          isAnonymous={!fromUser}
          userName={fromUser?.name || t('intention.anonymous-praying')}
          avatar={fromUser?.avatar ?? null}
          hasUplift={!!uplift}
          hasThanked={hasThanked || hasRepliedToUplift}
          index={index}
        />
        {uplift && (
          <View className="mt-3 max-w-full pl-16">
            <CollapsableNote
              className="max-w-full self-end"
              isThanked={hasThanked || hasRepliedToUplift}
              options={[
                {
                  onPress: () => onPressReportAbuseUplift(uplift),
                  children: t('app.report-abuse.uplift'),
                  startContent: <Icon name={ICON.ALERT_TRIANGLE} size="sm" />,
                  testID: TEST_ID.INTENTION.OPTIONS.REPORT_ABUSE,
                },
              ]}
              text={uplift.message}
            />
          </View>
        )}
      </>
    )
  }

  return (
    <FlatList<TPrayersApi>
      testID={testID}
      className="w-full bg-primary-bg-light"
      keyExtractor={keyExtractor}
      contentContainerClassName="w-full px-4 py-6 max-w-[400px] self-center web:transition-none"
      onEndReachedThreshold={0.1}
      initialNumToRender={20}
      data={data}
      onEndReached={onEndReached}
      renderItem={renderItem}
      ListEmptyComponent={ListEmptyComponent}
      ListHeaderComponent={ListHeaderComponent}
      ListFooterComponent={ListFooterComponent}
      ItemSeparatorComponent={ItemSeparatorComponent}
    />
  )
}
